import { Sparkles } from "lucide-react";

interface QuickAnswerProps {
  /** Short, direct answer to the page's primary question (1–3 sentences). */
  answer: string;
  /** Optional supporting bullets — kept short and citable. */
  bullets?: string[];
  /** Optional eyebrow above the heading. */
  eyebrow?: string;
  /** Override for the heading (default: "Quick answer"). */
  heading?: string;
}

/**
 * AEO / answer-engine block. Renders a short, citable answer near the top of
 * a content page so Google AI Overviews, ChatGPT, Perplexity, and Claude can
 * lift a clean response. The `speakable` class is referenced by the
 * SpeakableSpecification on schema-eligible pages for voice surfaces.
 */
export const QuickAnswer = ({
  answer,
  bullets,
  eyebrow = "TL;DR",
  heading = "Quick answer",
}: QuickAnswerProps) => (
  <aside
    aria-label="Quick answer"
    className="speakable not-prose mb-10 bg-gold/5 border-l-4 border-gold rounded-r-lg p-5 md:p-6"
  >
    <div className="flex items-center gap-2 text-gold text-[11px] font-bold uppercase tracking-[0.18em] mb-2">
      <Sparkles className="w-3.5 h-3.5" />
      {eyebrow}
    </div>
    <h2 className="font-heading text-lg md:text-xl font-bold text-navy mb-2 leading-tight">
      {heading}
    </h2>
    <p className="text-foreground/90 leading-relaxed text-base md:text-[17px]">
      {answer}
    </p>
    {bullets && bullets.length > 0 && (
      <ul className="mt-3 space-y-1.5 text-sm text-foreground/80">
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-2 leading-relaxed">
            <span className="text-gold font-bold mt-0.5">•</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
  </aside>
);
